import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuideDetail() {
  const { id } = useParams();
  const [guide, setGuide] = useState(null);

  // Set page title dynamically
  usePageTitle(guide?.title || 'Guide');

  useEffect(() => {
    fetchGuide();
  }, [id]);

  const fetchGuide = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guides/${id}`);
      setGuide(response.data);
    } catch (error) {
      console.error('Error fetching guide:', error);
    }
  };

  if (!guide) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Image */}
      <div className="relative h-[60vh] overflow-hidden">
        <img src={guide.image_url} alt={guide.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050C16] via-[#050C16]/70 to-transparent"></div>
      </div>

      <article className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <Link to="/guides" className="inline-flex items-center gap-2 text-gold hover:text-gold/80 mb-8">
          <ArrowLeft size={18} />
          <span className="text-sm uppercase tracking-wide">Back to Guides</span>
        </Link>

        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{guide.category}</p>
          <h1
            className="text-4xl md:text-6xl font-medium tracking-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="guide-title"
          >
            {guide.title}
          </h1>
          <div className="flex items-center gap-2 text-white/40 text-sm">
            <Clock size={14} />
            <span>{guide.read_time_minutes} min read</span>
          </div>
        </div>

        <div className="prose prose-invert prose-lg max-w-none">
          <p className="text-xl text-white/80 leading-relaxed mb-8 font-light">{guide.summary}</p>
          <div className="text-white/70 leading-relaxed whitespace-pre-line">{guide.content}</div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10">
          <Link to="/contact">
            <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] px-8 py-6 uppercase tracking-widest rounded-none">
              Get Expert Advice
            </Button>
          </Link>
        </div>
      </article>

      {/* Related Guides CTA */}
      <div className="bg-[#0A192F] py-24 mt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="text-3xl md:text-4xl font-medium mb-8"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Explore More Guides
          </h2>
          <Link to="/guides">
            <Button
              variant="outline"
              className="border-gold text-gold hover:bg-gold hover:text-[#0A192F] px-8 py-6 uppercase tracking-widest rounded-none"
            >
              All Guides
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
